import React, { lazy, Suspense, useState } from 'react'
import { Box, Typography, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useLocation } from 'react-router-dom'
import { useAuth } from 'hooks/auth/useAuth'
import { Timer, AutoAwesome } from '@mui/icons-material'
import { SessionExpiringSoon } from './SessionExpiringSoon'
import NotificationAccordion from './NotificationAccordion'
const ActionBanner = lazy(() => import('./ActionBanner'))
import { CoreAccountCluster } from './CoreAccountCluster'
import { CommandPalette } from 'components/command-palette/CommandPalette'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { useTimesheet, useInactivityClockOut } from 'hooks/timesheet'
import { DateTime } from 'luxon'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { ASSISTANT_FEATURE_FLAG_ID } from 'constants/featureFlags'
import { useAssistant } from 'components/assistant'
import { PortalAssistantOnboarding } from 'components/assistant/PortalAssistantOnboarding'
import { PortalAssistantToolsOnboarding } from 'components/assistant/PortalAssistantToolsOnboarding'
import { COMMAND_PALETTE_DEMO_ID } from 'components/command-palette/CommandPaletteOnboarding'
import { featureDemoStorageId, shouldShowFeatureDemo } from 'components/feature-demo/featureDemoStorage'
import { FEATURE_FLAGS } from '@/config/featureFlags'

export const CoreAppToolbar = () => {
  const location = useLocation()
  const { currentUser, userInfo } = useAuth()
  const theme = useTheme()
  const isPhone = useIsMobile()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const isOwner = userInfo?.isOwner
  const isClientContext = userInfo?.role === 'client' || location.pathname.startsWith('/client')
  const isAdminRole = userInfo?.role === 'admin' || userInfo?.role === 'babyAdmin'
  const canUseAssistant = userInfo?.role === 'admin'
  const assistantEnabled = useFeatureFlag(ASSISTANT_FEATURE_FLAG_ID)
  const assistant = useAssistant()
  const [assistantButton, setAssistantButton] = useState<HTMLButtonElement | null>(null)
  const [paletteDemoVisible, setPaletteDemoVisible] = useState<boolean | null>(null)
  const uid = currentUser?.uid || userInfo?.id
  const paletteStoragePending =
    Boolean(uid) &&
    shouldShowFeatureDemo(featureDemoStorageId(COMMAND_PALETTE_DEMO_ID, uid), {
      enabled: FEATURE_FLAGS.commandPaletteOnboarding && Boolean(isAdminRole),
    })
  const deferAssistantDemo = paletteDemoVisible ?? paletteStoragePending

  const {
    startTimer,
    stopTimer,
    isTimerActive,
    isSaving,
    isStarting,
    timerValue,
  } = useTimesheet()

  useInactivityClockOut()

  const handleClockIn = async () => {
    if (isMobile) return
    try {
      await startTimer({
        date: DateTime.now().toISODate(),
        startTime: DateTime.now().toISO(),
        duration: 0,
        userId: '',
        timerActive: true,
      })
    } catch (error) {
      console.error('Failed to start timer:', error)
    }
  }

  const handleClockOut = async () => {
    try {
      await stopTimer()
    } catch (error) {
      console.error('Failed to stop timer:', error)
    }
  }

  const handleTimerClick = async () => {
    if (!isTimerActive) {
      await handleClockIn()
    } else {
      await handleClockOut()
    }
  }

  return (
    <>
      <Box
        sx={{
          justifySelf: { xs: 'stretch', md: 'center' },
          width: { xs: '100%', md: 'clamp(280px, 52vw, 720px)' },
          minWidth: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CommandPalette
          isMobile={isMobile}
          isOwner={Boolean(isOwner)}
          isClientContext={isClientContext}
          isTimerActive={isTimerActive}
          onClockIn={handleClockIn}
          onClockOut={handleClockOut}
          onDemoVisibilityChange={setPaletteDemoVisible}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          justifySelf: 'end',
          gap: { xs: 0.5, sm: 1 },
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          flexWrap: 'nowrap',
        }}
      >
        <SessionExpiringSoon />
        {assistantEnabled && canUseAssistant && assistant ? (
          <Tooltip title='Ask assistant (/ask)'>
            <IconButton
              ref={setAssistantButton}
              color='inherit'
              onClick={() => assistant.openAssistant()}
              aria-label='Open portal assistant'
              sx={{
                color: 'inherit',
                '&:hover': {
                  backgroundColor: alpha(theme.custom.chromeContrast, 0.12),
                },
              }}
            >
              <AutoAwesome />
            </IconButton>
          </Tooltip>
        ) : null}
        {assistantEnabled && canUseAssistant && assistant ? (
          <>
            <PortalAssistantOnboarding
              anchorEl={assistantButton}
              isMobile={isMobile}
              defer={deferAssistantDemo}
              onOpenAssistant={assistant.openAssistant}
              onCloseAssistant={assistant.closeAssistant}
            />
            <PortalAssistantToolsOnboarding
              anchorEl={assistantButton}
              isMobile={isMobile}
              defer={deferAssistantDemo}
              onOpenAssistant={assistant.openAssistant}
            />
          </>
        ) : null}
        {!isOwner && !isClientContext && (!isPhone || isTimerActive) && (
          <Tooltip
            title={
              isTimerActive
                ? 'Clock Out'
                : isMobile
                  ? 'Clock in is not available on mobile devices'
                  : 'Clock In'
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={handleTimerClick}
                disabled={isSaving || isStarting || (!isTimerActive && isMobile)}
                color={isTimerActive ? 'success' : 'inherit'}
                aria-label={isTimerActive ? 'Clock out' : 'Clock in'}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha(theme.custom.chromeContrast, 0.12),
                  },
                  opacity: !isTimerActive && isMobile ? 0.5 : 1,
                }}
              >
                <Timer />
              </IconButton>
              {isTimerActive && (
                <Typography
                  variant='body2'
                  fontFamily='monospace'
                  color='success.main'
                  sx={{
                    minWidth: '60px',
                    fontSize: '0.875rem',
                    fontWeight: 'medium',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {timerValue}
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}
        {!isClientContext ? (
          <Suspense fallback={null}>
            <ActionBanner />
          </Suspense>
        ) : null}
        {!isPhone && !isClientContext ? <NotificationAccordion /> : null}
        {!isPhone ? <CoreAccountCluster /> : null}
      </Box>
    </>
  )
}
